/* Footer wrapper: carries the divider from the results above and stacks an optional
   submit-error Alert over the action row. Collapses to just the row when there's no
   error (the gap only applies between siblings, so no reserved whitespace). */
.footer {
  margin-top: 2px;
  padding-top: 12px;
  border-top: 1px solid var(--gray-border);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Bottom action row, mirroring .checksHeader at the top of the results card:
   run metrics on the left, actions on the right. Button positions are FIXED across
   states so muscle memory holds. */
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
}

.meta {
  font-size: 11px;
  color: var(--gray-text);
  opacity: 0.7;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid var(--gray-border);
  background: var(--white);
  color: var(--text);
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}

.btn svg { flex-shrink: 0; }

/* Trim the font's asymmetric half-leading so the label's box hugs cap-top→baseline.
   Without this, flex centers the em-box (not the glyphs) and the text reads ~1px high
   against the icon. Degrades gracefully: unsupporting browsers just get the old look. */
.btn span {
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
}

.btn:focus-visible {
  outline: 2px solid var(--blue-light);
  outline-offset: 2px;
}

.secondary:hover {
  border-color: var(--blue);
}

/* Primary CTA — the "period at the end of the paragraph". Fixed min-width so the
   slot never shifts as the label changes (Submit / Submitting… / Retry). */
.primary {
  background: var(--blue-light);
  border-color: var(--blue-light);
  color: var(--white);
  min-width: 122px;
  justify-content: center;
}

.primary:hover:not(:disabled) {
  background: var(--blue);
  border-color: var(--blue);
}

.btn:disabled { cursor: not-allowed; }

.primary:disabled:not(.submitted) {
  background: #aeb8c2;
  border-color: #aeb8c2;
  color: #eef1f4;
}

/* Submitted: same footprint as .primary so nothing shifts. */
.submitted {
  background: var(--green-bg);
  border-color: var(--green);
  color: var(--green);
  cursor: default;
  min-width: 122px;
  justify-content: center;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: var(--white);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .spinner { animation: none; } }

/* Confirm popover — opens upward from the Submit button (row is at the bottom). */
.submitWrap { position: relative; }

.popover {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 244px;
  background: var(--white);
  border: 1px solid var(--gray-border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(1, 42, 72, 0.18);
  padding: 12px;
  font-size: 12.5px;
  color: var(--text);
  z-index: 5;
  text-align: left;
}

.popover p { margin: 0 0 10px; }

.popoverButtons { display: flex; gap: 8px; justify-content: flex-end; }

.confirm {
  background: var(--blue-light);
  color: var(--white);
  border: none;
  border-radius: var(--radius);
  padding: 5px 12px;
  font: inherit;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
}

.confirm:hover { background: var(--blue); }

.cancel {
  background: none;
  border: 1px solid var(--gray-border);
  border-radius: var(--radius);
  padding: 5px 12px;
  font: inherit;
  font-size: 12px;
  color: var(--gray-text);
  cursor: pointer;
}

/* Arrow pointing down at the Submit button. */
.popover::after {
  content: "";
  position: absolute;
  bottom: -6px;
  right: 26px;
  width: 10px;
  height: 10px;
  background: var(--white);
  border-right: 1px solid var(--gray-border);
  border-bottom: 1px solid var(--gray-border);
  transform: rotate(45deg);
}
